import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Rocket, Loader2, Sparkles } from "lucide-react";
import { api, apiError, BACKEND_URL } from "../lib/api";

const statusPill = (ok, processing, error, labels) => {
  const cls = ok ? "border-green-500/40 text-green-400"
    : processing ? "border-yellow-400/40 text-yellow-300"
    : error ? "border-red-500/40 text-red-400" : "border-zinc-600 text-zinc-500";
  const label = ok ? labels[0] : processing ? labels[1] : error ? "error" : "pendiente";
  return <span className={`px-2 py-0.5 rounded-full border text-[11px] ${cls}`}>{label}</span>;
};

const isReady = (m) => {
  if (m.type === "video") return !!m.video_url;
  if (m.type === "image") return !!m.image_url;
  if (m.type === "text") return !!m.caption;
  if (m.type === "whatsapp") return (m.variants || []).length === 3;
  return !!(m.landing && m.landing.title);
};

export default function AdminMultinivel() {
  const [materials, setMaterials] = useState([]);
  const [busy, setBusy] = useState(null);
  const [busyAll, setBusyAll] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/renalife/multinivel");
      setMaterials(data);
      if (data.some((m) => m.video_status === "processing")) {
        clearTimeout(timer.current);
        timer.current = setTimeout(load, 8000);
      }
    } catch (err) { console.error("Error cargando multinivel:", err); }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  const generate = async (materialId) => {
    setBusy(materialId);
    try {
      const { data } = await api.post(`/admin/multinivel/${materialId}/generate`);
      setMaterials(data);
      const m = data.find((x) => x.material_id === materialId);
      toast.success(m?.type === "video" ? "Video largo en proceso (5-10 min)…" : "Generado correctamente");
      clearTimeout(timer.current);
      timer.current = setTimeout(load, 8000);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const generateAll = async () => {
    setBusyAll(true);
    for (const m of materials) {
      setBusy(m.material_id);
      try {
        const { data } = await api.post(`/admin/multinivel/${m.material_id}/generate`);
        setMaterials(data);
      } catch (err) {
        toast.error(`${m.title}: ${apiError(err.response?.data?.detail)}`);
      }
    }
    setBusy(null);
    setBusyAll(false);
    toast.success("Campaña MULTINIVEL lanzada. Los videos largos siguen generándose en segundo plano.");
    clearTimeout(timer.current);
    timer.current = setTimeout(load, 8000);
  };

  return (
    <div className="mt-12" data-testid="admin-multinivel-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Rocket className="text-[#FACC15]" size={22} />
          <h2 className="font-display font-bold text-xl">Campaña MULTINIVEL (RENALIFE)</h2>
        </div>
        <button
          data-testid="mn-generate-all"
          onClick={generateAll}
          disabled={busyAll}
          className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-4 py-2.5 bg-[#FACC15] hover:bg-yellow-300 text-[#022C22] disabled:opacity-50"
        >
          {busyAll ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} GENERAR TODA LA CAMPAÑA
        </button>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Videos largos (30-45 seg, varios clips unidos con voz IA), imágenes, textos y landing. Se genera UNA vez y todos los distribuidores RENALIFE la ven lista. Costo aprox. por video largo: ~$2-3 USD en fal.ai.
      </p>
      <div className="space-y-4">
        {materials.map((m) => (
          <div key={m.material_id} data-testid={`admin-mn-${m.material_id}`} className="rounded-2xl border border-[#FACC15]/25 bg-[#04231b] p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display font-bold">{m.title}</h3>
                <p className="text-xs text-zinc-400 mt-0.5">{m.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  {statusPill(isReady(m), m.video_status === "processing" && !m.video_stuck, m.video_status === "error" || m.video_stuck, ["listo", "generando…"])}
                  {m.video_status === "processing" && !m.video_stuck && m.video_progress && (
                    <span className="text-[11px] text-yellow-300">{m.video_progress}</span>
                  )}
                </div>
                {m.video_stuck && (
                  <p className="text-[11px] text-orange-400 mt-1">
                    ⚠️ Lleva demasiado tiempo sin avanzar; el proceso pudo interrumpirse (reinicio del servidor). Presiona "Reintentar".
                  </p>
                )}
                {m.video_status === "error" && <p className="text-[11px] text-red-400 mt-1">Error: {m.video_error}</p>}
              </div>
              <button
                data-testid={`mn-gen-${m.material_id}`}
                onClick={() => generate(m.material_id)}
                disabled={busy === m.material_id || (m.video_status === "processing" && !m.video_stuck)}
                className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-4 py-2 border border-[#FACC15]/50 text-[#FACC15] hover:bg-[#FACC15]/10 disabled:opacity-50 shrink-0"
              >
                {busy === m.material_id ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />}
                {m.video_stuck || m.video_status === "error" ? " Reintentar" : " Generar"}
              </button>
            </div>
            {m.image_url && <img src={`${BACKEND_URL}${m.image_url}`} alt={m.title} className="mt-4 rounded-xl border border-white/10 max-h-56 object-cover" />}
            {m.video_url && <video src={m.video_url} controls className="mt-3 rounded-xl border border-white/10 max-h-64 bg-black" />}
            {m.caption && <p className="mt-3 text-xs text-zinc-300 whitespace-pre-wrap bg-black/30 border border-white/10 rounded-xl p-3">{m.caption}</p>}
            {(m.variants || []).map((v, i) => (
              <p key={v} className="mt-2 text-xs text-zinc-300 whitespace-pre-wrap bg-black/30 border border-white/10 rounded-xl p-3"><span className="text-[#FACC15] font-bold">Variante {i + 1}: </span>{v}</p>
            ))}
            {m.landing?.title && (
              <div className="mt-3 text-xs text-zinc-300 bg-black/30 border border-white/10 rounded-xl p-3 space-y-1">
                <p><span className="text-[#FACC15] font-bold">Título: </span>{m.landing.title}</p>
                <p><span className="text-[#FACC15] font-bold">Subtítulo: </span>{m.landing.subtitle}</p>
                {(m.landing.benefits || []).map((b) => <p key={b.title}>• <span className="font-semibold">{b.title}:</span> {b.text}</p>)}
                <p><span className="text-[#FACC15] font-bold">CTA: </span>{m.landing.cta}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
